import React, { useState, useEffect, useRef } from "react";
import { pdfjs } from "react-pdf";
import { useTranslation } from "react-i18next";
import axios from "axios";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { useAuth } from "react-oidc-context";
import { Button } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  backendUrls,
  backendUrl,
  customerName,
  apiVersion,
} from "./constants/constants";
import DownloadButton from "./components/Buttons/DownloadButton";
import PDFViewer from "./components/Surface/PdfViewer";
import UnauthorizedPopup from "./components/Feedback/UnauthorizedPopup";
import PopupError from "./errors/PopupError";
import CommentFeature from "./components/Surface/CommentFeature";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url
).toString();

function Main() {
  const [pdf, setPdfs] = useState("");
  const { t } = useTranslation();
  const [numberOfPages] = useState(0);
  const [FRONTEND, setFRONTEND] = useState("");
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [displayPermissionsButton, setDisplayPermissionsButton] =
    useState(false);
  const [maxPageWidth, setMaxPageWidth] = useState(800); // Adjust this value as needed
  const [maxPageHeight, setMaxPageHeight] = useState(900); // Adjust this value as needed
  const [displayError, setDisplayError] = useState({
    display: false,
    title: "",
    description: "",
  });

  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const configUrl = backendUrls.configUrl;
  const pdfMergeUrl = backendUrls.pdfMergeUrl;
  const { applicationId } = useParams();
  const { language } = useParams();
  const token = auth.user?.id_token;
  const { targetUser } = useParams();

  const containerRef = useRef();
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      sessionStorage.setItem("beforeLogin", location.pathname);
      navigate("/callback");
      return;
    }

    if (!isConfigLoading) {
      const customerName =
        sessionStorage.getItem("customerName") ||
        window.location.host.split(".")[0];
      fetch(configUrl + `?language=${language}&customerName=${customerName}`)
        .then((response) => response.json())
        .then((data) => setFRONTEND(data));
      setIsConfigLoading(true);
    }

    if (!isPdfLoading) {
      const token = auth.user?.id_token;
      const customerName =
        sessionStorage.getItem("customerName") ||
        window.location.host.split(".")[0];

      const fetchDataUrl = `${pdfMergeUrl}?applicationId=${applicationId}&language=${language}&customerName=${customerName}&targetUser=${targetUser}&token=${token}`;

      const apiUrl = `${backendUrl}/${apiVersion}/PDFMerge`;
      axios
        .get(apiUrl, {
          headers: {
            "Content-Type": "application/json",
            applicationId: applicationId,
            language: language,
            customerName: customerName || window.location.host.split(".")[0],
            targetUser: targetUser,
            Authorization: `Bearer ${token}`,
          },
        })
        .then(async (response) => {
          if (response.statusText !== "OK") {
            // Retrieve any error text sent by the server
            console.log(
              `fetch failed with status ${
                response.status
              } and message ${await response.data}`
            );
            let title, description;

            // Choose the appropriate title and description based on the status code
            if (response.status === 401) {
              title = t("unauthorizedPopup.title");
              description = t("unauthorizedPopup.description");
            } else if (response.status === 403) {
              title = t("forbiddenPopup.title");
              description = t("forbiddenPopup.description");
            } else if (response.status === 478) {
              //custom error code for anonymized applications
              title = t("anonymizedPopup.title");
              description = t("anonymizedPopup.description");
            } else if (response.status === 500) {
              title = t("serverErrorPopup.title");
              description = t("serverErrorPopup.description");
            } else {
              // For other errors, use the server's error message if available
              title = t("genericErrorPopup.title");
              description = t("genericErrorPopup.description");
            }

            // Throw a custom error object that includes both title and description
            throw new PopupError(title, description, response.status);
          }
          return response.data;
        })
        .then((responseText) => {
          if (!responseText) {
            throw new PopupError(
              t("serverErrorPopup.title"),
              t("serverErrorPopup.description")
            );
          }

          if (!responseText) {
            throw new PopupError(
              t("serverErrorPopup.title"),
              t("serverErrorPopup.description")
            );
          }
          // If everything is valid, update state

          setPdfs(responseText.mergedPDF);
        })
        .catch((error) => {
          // throw the error message as per the error code returned from backend
          console.log("error checking:", error);
          if (error?.response?.status === 401) {
            setDisplayError({
              display: true,
              title: t("unauthorizedPopup.title"),
              description: t("unauthorizedPopup.description"),
            });
          } else if (error?.response?.status === 403) {
            setDisplayError({
              display: true,
              title: t("forbiddenPopup.title"),
              description: t("forbiddenPopup.description"),
            });
          } else if (error?.response?.status === 478) {
            setDisplayError({
              display: true,
              title: t("anonymizedPopup.title"),
              description: t("anonymizedPopup.description"),
            });
          } else if (error?.response?.status === 500) {
            setDisplayError({
              display: true,
              title: t("serverErrorPopup.title"),
              description: t("serverErrorPopup.description"),
            });
          } else {
            setDisplayError({
              display: true,
              title: t("genericErrorPopup.title"),
              description: t("genericErrorPopup.description"),
            });
          }
        });

      setIsPdfLoading(true);
    }

    if (FRONTEND?.Theming?.Images?.FavIcon) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.getElementsByTagName("head")[0].appendChild(link);
      }
      link.href = FRONTEND.Theming.Images.FavIcon;
    }

    const updatePageWidth = () => {
      if (containerRef.current) {
        setMaxPageWidth(Math.min(containerRef.current.offsetWidth - 20, 800)); // Set the max width
        setMaxPageHeight(Math.min(window.innerHeight - 20, 600)); // Set the max height
      }
    };

    updatePageWidth();
    window.addEventListener("resize", updatePageWidth);

    if (!hasLoaded.current) {
      checkDisplayPermissionButton();
      checkDownloadButton();
      hasLoaded.current = true;
    }

    return () => {
      window.removeEventListener("resize", updatePageWidth);
    };
  }, [
    auth,
    applicationId,
    language,
    targetUser,
    configUrl,
    navigate,
    pdfMergeUrl,
    pdf,
    numberOfPages,
    FRONTEND,
    isPdfLoading,
    isConfigLoading,
    location.pathname,
  ]);

  const checkDisplayPermissionButton = async () => {
    try {
      const apiUrl = `${backendUrl}/${apiVersion}/permissionsButton`;
      const response = await axios.get(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          applicationId: applicationId,
          customerName: customerName || window.location.host.split(".")[0],
          Authorization: `Bearer ${token}`,
        },
      });
      setDisplayPermissionsButton(response.data.showManagePermissionsButton);
    } catch (error) {
      console.log("error in permission button:", error);
    }
  };
  const checkDownloadButton = async () => {
    try {
      const apiUrl = `${backendUrl}/${apiVersion}/DownloadButton`;
      const response = await axios.get(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          customerName: customerName || window.location.host.split(".")[0],
          Authorization: `Bearer ${token}`,
        },
      });
      setShowDownloadButton(response.data.showDownloadButton);
    } catch (error) {
      console.log("error in permission button:", error);
    }
  };

  const handlePermissionClick = () => {
    navigate(`/permissions/${applicationId}/${language}`);
  };

  if (!auth.isAuthenticated) return <div />;
  const pdfDocumentBackground = {
    paddingBottom: "20px",
    backgroundColor: "black",
    minHeight: "110vh",
  };

  const headerContainer = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100px",
  };

  const imageStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  };

  const logoStyle = {
    width: "auto",
    height: "50px",
    position: "absolute",
  };

  const pdfContainer = {
    width: "100%",
    maxWidth: "800px",
    margin: "0 auto",
  };

  const buttonStyle = {
    backgroundColor: "#4CAF50",
    backgroundColor: `${
      FRONTEND?.Theming?.buttonStyle?.backgroundColor || "#4CAF50"
    }`,
    border: "none",
    color: `${FRONTEND?.Theming?.buttonStyle?.color || "#ffffff"}`,
    padding: "7px 15px",
    textAlign: "center",
    textDecoration: "none",
    display: "inline-block",
    fontSize: "12px",
    margin: "4px 2px",
    cursor: "pointer",
    borderRadius: "8px",
    transition: "background-color 0.3s",
    textTransform: "none",
    filter: isHovered ? "brightness(70%)" : "none",
  };

  let customerLogo;
  try {
    customerLogo = FRONTEND.Theming.customerAssets.logo;
  } catch {}

  let customerHeader;
  try {
    customerHeader = FRONTEND.Theming.customerAssets.header;
  } catch {}

  return (
    <div style={pdfDocumentBackground}>
      <div style={headerContainer}>
        <img
          src={customerLogo}
          alt="header"
          style={
            FRONTEND?.Theming?.customerLogoStyle
              ? FRONTEND?.Theming?.customerLogoStyle
              : logoStyle
          }
        />
        <img src={customerHeader} alt="header" style={imageStyle} />
      </div>
      <div style={pdfContainer}>
        <div
          style={{
            margin: "16px 0px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {displayPermissionsButton && (
            <Button
              variant="contained"
              mr={2}
              sx={buttonStyle}
              onClick={handlePermissionClick}
              onMouseOver={() => setIsHovered(true)}
              onMouseOut={() => setIsHovered(false)}
            >
              {t("buttons.permission")}
            </Button>
          )}
          {showDownloadButton && (
            <DownloadButton
              base64String={pdf}
              fileName={"application_" + applicationId + ".pdf"}
              backgroundColor={FRONTEND?.Theming?.buttonStyle?.backgroundColor}
              color={FRONTEND?.Theming?.buttonStyle?.color}
            />
          )}
        </div>
        <div style={{ display: "flex" }}>
          <PDFViewer
            data={pdf}
            maxHeight={maxPageHeight}
            maxWidth={maxPageWidth}
          />
        </div>
        {FRONTEND?.enableCommentFeature && <CommentFeature />}
      </div>
      <UnauthorizedPopup
        open={displayError.display}
        title={displayError.title}
        description={displayError.description}
      />
    </div>
  );
}

export default Main;
